import React, { useState, useReducer, useEffect } from 'react';

import './Pagos.scss';
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import SectionTitleBar from '../components/titlebar';
import { PagoRequest } from '../../models/Pago';
import moment from 'moment';

import Button from '../Forms/Button';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { graphqlSchema } from '../../services/graphql.schema';
import { findCfdiByUuid } from '../../services/fiscalpopApi';
import { ToBillEvent, ToBillArguments, FiscalpopCFDI } from '../../models/Factura';
import PagoTotales from './components/Pagos.totales';
import PagosReceptor from './components/Pagos.receptor';
import PagosRelacionadas from './components/Pagos.relacionadas';
import PagosFindModal from './components/Pagos.findModal';
import { validatePagoIsComplete } from '../../services/validation.cfdi';

import { clearGqlTypename } from '../../utils/formatting';
import { Receptor, SerieTracker } from '../../models/Catalogos';
import { useParams } from 'react-router-dom';
import { ArgumentBinded, buildFacturasRelacionadas, pagoReducer, SET_EMPTY_PAGO_FORM } from './handlers/pago.state';
import { recoverPaymentToEdit } from './handlers/pago.editRecover';
import { usePagoStamping } from './handlers/usePagoStamping';
import { useReceptorSearch } from '../Hooks/useReceptorSearch';

interface PagosProps extends BaseReact {
    currentUser: ProfileQL;
}

type PagoLocationState = { billArgument: ToBillArguments<ToBillEvent>; paymentEvents: ToBillEvent[]; };

/** State is only usable when it carries both the bill argument and its payment events */
function getPagoLocationState(state: unknown): PagoLocationState {
    const _state = state as PagoLocationState;
    return !!_state && !!_state.billArgument && !!_state.paymentEvents ? _state : null;
}

function Pagos({ currentUser, location, history }: PagosProps) {
    const { eventid } = useParams<{ eventid?: string }>();

    const [validRequested, setValidRequested] = useState(false);
    const [externalBill, setExternalBill] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isStamping, setIsStamping] = useState(false);
    const [findingExecuted, setFindingExecuted] = useState(false);

    const [displayNoRfcWarning, setDisplayNoRfcWarning] = useState(false);

    // Autocomplete states ---
    const [tracekrOptions, setTrackerOptions] = useState<SerieTracker[]>([]);
    const { receptorOptions, searchReceptorByName, searchReceptorByRfc } = useReceptorSearch({ clearTypename: false });
    const [formasPago, setFormasPago] = useState<{ value: string, label: string }[]>([]);

    // Functional States ------
    const [binderIndexes, setBinderIndexes] = useState<string[]>([])
    const [bindersSelected, setBindersSelected] = useState<ArgumentBinded[]>([])
    const [argBillBinding, setArgBillBinding] = useState<ArgumentBinded[]>([])
    const [cfdi, dispatchPago] = useReducer(pagoReducer, SET_EMPTY_PAGO_FORM(currentUser));

    // ==============
    const [searchRelated] = useLazyQuery(graphqlSchema.FISCALPOP.CFDI.getOpenBillArgumentsByRFC, {
        onCompleted: ({ getOpenBillArgumentsByRFC }: { getOpenBillArgumentsByRFC: { argument: ToBillArguments<ToBillEvent>, pendingPayment: number }[] }) => {
            // Only look for arguments with paymentEvents
            const availableArgs = getOpenBillArgumentsByRFC.filter(args => !!args.argument.paymentEvents.length && !!args.argument.uuid);
            console.log('[searchRelated] availableArgs: ', availableArgs);
            const cfdiProms = availableArgs.map((args) => {
                return findCfdiByUuid(currentUser.professionalProfile.authToken, args.argument.uuid)
                    .then((cfdi: FiscalpopCFDI[]) => (
                        { argument: args.argument, cfdi: cfdi[0], insoluto: args.pendingPayment }
                    ))
            })
            Promise.all(cfdiProms).then((_binded) => {
                console.log('[searchRelated] BINDED: ', _binded);

                const binded = _binded.filter(b => b.argument.paymentEvents.some(pE => !pE.isClosed))
                setIsLoading(false);
                // IF LOCATION STATE EXISTS, SET
                const locationState = getPagoLocationState(location.state);
                if (locationState) {
                    const { billArgument, paymentEvents } = locationState;
                    const { applicableBinder, argumentId, pago } = recoverPaymentToEdit(binded, billArgument, paymentEvents, eventid);
                    setBindersSelected([applicableBinder]);
                    setBinderIndexes([argumentId])
                    dispatchPago({ property: 'pago', value: pago });
                }
                setArgBillBinding(binded);
                setFindingExecuted(true);
            })
        },
        onError: (e) => {
            console.error('Error fetching CFDI Bindings: ', e);
        },
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        // Extract state and use for current operations
        const locationState = getPagoLocationState(location.state);
        if (locationState) {
            const { billArgument } = locationState;
            dispatchPago({ property: 'receptor', value: clearGqlTypename(billArgument.MasterCFDI.receptor) });
            // get relative CFDI from argument
            console.log(`<PAGO> UE state: `, location.state)
            searchRelated({
                variables: {
                    rfc: billArgument.MasterCFDI.receptor.rfc,
                    argumentId: billArgument._id
                }
            })
        }

    }, [location, searchRelated, currentUser]);

    // GRAPHQL ----------------
    useQuery(graphqlSchema.PROFILE.CATALOGOS.getAllTrackers, {
        onCompleted: ({ getAllTrackers }: { getAllTrackers: SerieTracker[] }) => {
            setTrackerOptions(getAllTrackers.filter(t => !t.receptorRfc));
        },
        onError: (e) => {
            console.error('Error getting by name: ', e.graphQLErrors[0])
        },
        fetchPolicy: 'cache-and-network'
    })
    useQuery(graphqlSchema.FISCALPOP.FORMA_PAGO.getFormaPago, {
        onCompleted: ({ getFormaPago }: { getFormaPago: { key: string, label: string }[] }) => {
            setFormasPago(getFormaPago.map(gFP => ({ value: gFP.key, label: `(${gFP.key}) ${gFP.label}` })));
        },
        onError: (e) => {
            console.error('Error getting uso CFDI: ', e);
        }
    })
    const { stampPayment, restampPayment } = usePagoStamping(history, () => setIsStamping(false));

    // Handlers ============

    const clearForm = () => {
        setBindersSelected([])
        setBinderIndexes([])
        setArgBillBinding([])
        dispatchPago({ property: '_replace', value: SET_EMPTY_PAGO_FORM(currentUser) });
        if (eventid) {
            history.push({
                pathname: `/facturacion/setup/pagos`,
                state: {
                }
            })
        }
    }

    const handlePagoReceptorChange = (property: 'nombre' | 'rfc' | 'zip' | 'regimen') => (value: any) => {
        const _receptor = cfdi.receptor;
        if (property === 'rfc') {
            value = value.toUpperCase();
        }
        _receptor[property] = value;
        dispatchPago({ property: 'receptor', value: _receptor });
        if (property === 'nombre') {
            searchReceptorByName(value);
        }
        if (property === 'rfc') {
            searchReceptorByRfc(value);
        }
    }

    const handleReceptorAutofill = (receptorAsString: string) => {
        const receptor: Receptor = JSON.parse(receptorAsString);
        const _receptor = Object.assign({}, cfdi.receptor, { nombre: receptor.name, rfc: receptor.rfc, customerId: receptor._id });
        if (receptor.email) {
            _receptor.email = receptor.email;
        }
        if (receptor.zip) {
            _receptor.zip = receptor.zip;
        }
        if (receptor.regimen) {
            _receptor.regimen = receptor.regimen;
        }
        const comprobante: PagoRequest = Object.assign({}, cfdi);
        comprobante.receptor = _receptor;
        if (receptor.seriePreference) {
            // Pull serie ref
            comprobante.serie = receptor.seriePreference;
            // get Tracker reference for folio
            const hasFolio = tracekrOptions.find(tO => tO.serie === receptor.seriePreference);
            if (!!hasFolio) {
                comprobante.folio = `${hasFolio.folio}`;
            }
        }
        dispatchPago({ property: 'comprobante', value: comprobante });
    }

    const handlePagoSerieBlur = (value: string) => {
        const hasFolio = tracekrOptions.find(tO => tO.serie === value);
        console.log('Serie: ', value, hasFolio)
        if (!!hasFolio) {
            dispatchPago({ property: 'comprobante', value: { folio: hasFolio.folio } });
        }
    }

    const handlePagoComprobanteChange = (property: 'serie' | 'folio') => (value: any) => {
        const _comp: { [key: string]: any } = {};
        _comp[property] = value;
        dispatchPago({ property: 'comprobante', value: _comp });
    }

    const handlePagoMainChange = (property: 'fecha' | 'monto' | 'formaDePago') => (value: any) => {
        const _pago: any = cfdi.pagos[0];
        if (property === 'fecha') {
            // >> Since FiscalPOP API takes pago's fecha as a string, we need to keep it as JSON string otherwise it will be converted to UTC
            _pago['fecha'] = moment(value as Date).startOf('day').toDate().toJSON();
        } else {
            _pago[property] = value;
        }
        dispatchPago({ property: 'pago', value: _pago });
    }

    const handleMontoPaidChange = (index: number) => (value: number) => {
        const binder = bindersSelected[index];
        if (value < 0.01) {
            value = 0.01
        }
        if (value > binder.insoluto) {
            value = binder.insoluto;
        }
        const pago = cfdi.pagos[0]
        pago.facturaRelacionada[index].importePagado = value;
        // Correct monto?
        pago.monto = pago.facturaRelacionada.reduce((p, c) => p + c.importePagado, 0);
        dispatchPago({ property: 'pago', value: pago });
    }

    const submitPreview = () => {
        console.log('PREVIEW PAGO (billArgumento selected): ', bindersSelected)
        console.log('PREVIEW PAGO (cfdi): ', cfdi);
        // Confirm that CFDI is correct else, state error
        if (!validatePagoIsComplete(cfdi)) {
            setValidRequested(true);
            return;
        }
        setIsStamping(true)
        const stampedArguments = bindersSelected.map(b => {
            const stArg: ToBillArguments<string> = clearGqlTypename(Object.assign({}, b.argument, { paymentEvents: b.argument.paymentEvents.map(pE => pE._id), billEvents: b.argument.billEvents.map(bE => bE._id) }));
            return stArg;
        });

        if (eventid) {
            restampPayment({
                variables: {
                    eventid,
                    arguments: stampedArguments,
                    cfdi: cfdi
                }
            })
        } else {
            stampPayment({
                variables: {
                    arguments: stampedArguments,
                    cfdi: cfdi
                }
            })
        }
    }

    // MODAL METHODS ------
    const closeBillFindModal = () => {
        setModalOpen(false);
    }

    const relateWithExternal = () => {
        setModalOpen(false);
        setExternalBill(true);
    }

    const openBillFindModal = () => {
        if (!cfdi.receptor.rfc) {
            if (!displayNoRfcWarning) {
                // IF guard blocks multi-click effects
                setDisplayNoRfcWarning(true);
                setTimeout(() => {
                    setDisplayNoRfcWarning(false);
                }, 6000)
            }
            return sharedToasterSubject.next({ type: 'warning', message: 'Agrega el RFC y nombre antes de buscar facturas relacionadas' })
        }
        setModalOpen(true);
        setFindingExecuted(false);
        searchBillBindinds()
    }

    const searchBillBindinds = () => {
        setIsLoading(true);
        setFindingExecuted(false);
        searchRelated({
            variables: {
                rfc: cfdi.receptor.rfc
            }
        })
    }

    const checkSelected = (index: string) => () => {
        if (binderIndexes.includes(index)) {
            setBinderIndexes(binderIndexes.filter(i => i !== index));
        } else {
            setBinderIndexes(binderIndexes.concat([index]));
        }
    }

    const selectAndCloseModal = () => {
        const binders = argBillBinding.filter((arg) => binderIndexes.includes(arg.argument._id));
        setBindersSelected(binders)
        const pago = cfdi.pagos[0];
        const { facturaRelacionada, monto } = buildFacturasRelacionadas(binders, pago.facturaRelacionada, pago.monto);
        dispatchPago({ property: 'pago', value: Object.assign(pago, { facturaRelacionada, monto }) });
        closeBillFindModal()
    }

    // ===============

    return (
        <div id="Pagos">
            <SectionTitleBar currentUser={currentUser} title={eventid ? `Editar Complemento de pagos` : `Complemento de pagos`} />
            <div className="pagosContent">
                <PagosReceptor
                    cfdi={cfdi}
                    validRequested={validRequested}
                    displayNoRfcWarning={displayNoRfcWarning}
                    receptorOptions={receptorOptions}
                    formasPago={formasPago}
                    onReceptorChange={handlePagoReceptorChange}
                    onReceptorAutofill={handleReceptorAutofill}
                    onComprobanteChange={handlePagoComprobanteChange}
                    onSerieBlur={handlePagoSerieBlur}
                    onPagoChange={handlePagoMainChange}
                />
                <PagosRelacionadas
                    cfdi={cfdi}
                    bindersSelected={bindersSelected}
                    externalBill={externalBill}
                    displayNoRfcWarning={displayNoRfcWarning}
                    onMontoPaidChange={handleMontoPaidChange}
                    onAddRelated={openBillFindModal}
                />
                <PagoTotales cfdi={cfdi} />
                <div className="submitactions">
                    {
                        validRequested ?
                            <p className="message error">
                                <span className="error">
                                    Algunos campos faltan de llenarse o contienen errores
                                </span>
                            </p>
                            : ''
                    }
                    <Button secondary={true} handleClick={clearForm}>
                        <span>
                            Limpiar
                        </span>
                    </Button>
                    <Button primary={true} handleClick={submitPreview} disabled={!bindersSelected.length || isStamping}>
                        {
                            isStamping ?
                                <span>
                                    Emitiendo factura
                                </span>
                                :
                                <span>
                                    Emitir Complemento
                                </span>
                        }

                    </Button>
                </div>
            </div>
            <PagosFindModal
                isOpen={modalOpen}
                cfdi={cfdi}
                isLoading={isLoading}
                findingExecuted={findingExecuted}
                argBillBinding={argBillBinding}
                binderIndexes={binderIndexes}
                eventid={eventid}
                onRequestClose={closeBillFindModal}
                onSearch={searchBillBindinds}
                onCheckSelected={checkSelected}
                onSelectAndClose={selectAndCloseModal}
                onRelateWithExternal={relateWithExternal}
            />
        </div>
    )
}

export default Pagos;
